import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { router } from './router';
import { IndexedDBService } from './services/storage/IndexedDBService';
import { SyncService } from './services/sync/SyncService';
import { migrateAllZustandStores } from './services/storage/zustandIndexedDBStorage';
import { PerformanceProvider } from './contexts/PerformanceContext';
import { createQueryClient, setQueryClientInstance } from './hooks/queries/queryConfig';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';

// === GESTIONNAIRES D'ERREURS GLOBAUX ===
// Patterns d'erreurs à ignorer (extensions de navigateur, erreurs réseau, CSP)
const ignoredErrorPatterns = [
  // Extensions de navigateur
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'extension://',
  'onboarding',
  'onboarding.js',
  // Erreurs réseau
  'ResizeObserver loop',
  'Non-Error promise rejection',
  'ERR_CONNECTION_RESET',
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'Failed to fetch',
  'Load failed',
  'NetworkError',
  'net::ERR_',
  // Erreurs de connexion Chrome
  'Could not establish connection',
  'Receiving end does not exist',
  // Erreurs CSP (Content Security Policy)
  'Content Security Policy',
  'violates the following Content Security Policy',
  // Erreurs de chargement de modules
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
];

// Vérifie si une erreur doit être ignorée
function shouldIgnoreError(message: string): boolean {
  return ignoredErrorPatterns.some(pattern => 
    message.toLowerCase().includes(pattern.toLowerCase())
  );
}

// Gestionnaire d'erreurs JavaScript globales
window.onerror = function(message, source, lineno, colno, error) {
  const errorMessage = String(message);
  const errorSource = String(source || '');
  
  // Ignorer les erreurs d'extensions et de réseau
  if (shouldIgnoreError(errorMessage) || shouldIgnoreError(errorSource)) {
    console.warn('[WanzoCompta] Erreur ignorée (extension/réseau):', errorMessage);
    return true; // Empêche l'erreur de se propager
  }
  
  console.error('[WanzoCompta] Erreur globale:', { message, source, lineno, colno, error });
  return false;
};

// Gestionnaire de promesses rejetées non gérées
window.onunhandledrejection = function(event) {
  const reason = event.reason;
  const errorMessage = reason?.message || String(reason) || '';
  const errorStack = reason?.stack || '';
  
  // Ignorer les erreurs d'extensions et de réseau
  if (shouldIgnoreError(errorMessage) || shouldIgnoreError(errorStack)) {
    console.warn('[WanzoCompta] Promesse rejetée ignorée (extension/réseau):', errorMessage);
    event.preventDefault(); // Empêche l'erreur de se propager
    return;
  }
  
  console.error('[WanzoCompta] Promesse rejetée non gérée:', reason);
};

// Créer l'instance QueryClient avec la configuration optimisée
const queryClient = createQueryClient();
setQueryClientInstance(queryClient);

// Initialize services that don't depend on auth
async function initializeServices() {
  try {
    await IndexedDBService.initDB();
    // Migrate any existing Zustand data from localStorage to IndexedDB
    await migrateAllZustandStores();
    await SyncService.initialize();
    console.log('✅ Base services initialized with TanStack Query');
  } catch (error) {
    console.error('Failed to initialize base services:', error);
  }
}

initializeServices().then(() => {
  const container = document.getElementById('root');
  if (!container) throw new Error('Failed to find the root element');
  const root = createRoot(container);

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Auth0Provider
            domain={domain}
            clientId={clientId}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            authorizationParams={{
              redirect_uri: redirectUri,
              audience: audience,
              scope: `${import.meta.env.VITE_AUTH0_SCOPE} offline_access`,
            }}
          >
            <PerformanceProvider>
              <RouterProvider router={router} />
            </PerformanceProvider>
          </Auth0Provider>
          {/* DevTools visible uniquement en développement */}
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
});